import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Settings, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const EMPTY = {
  title: "", short_description: "", description: "", category: "SCIENCE + NATURE", subject: "",
  images: "", date: "", time: "", location: "", address: "", capacity: 20, age_min: 8, age_max: 14,
  grade_range: "", member_price: 0, non_member_price: 49, estimated_cost: 0, transport_enabled: false,
  transport_price: 25, credit_eligible: false, chaperones_needed: 0, scholarship_eligible: false,
  visibility: "public", what_to_bring: "", lunch_info: "", cancellation_policy: "", experience_type: "standard_local",
};

const EXP_TYPES = [
  { v: "standard_local", label: "Standard Local Experience" },
  { v: "day_explorer", label: "H.E.A. Day Explorer" },
  { v: "signature", label: "H.E.A. Signature Experience" },
];

export default function AdminExperiences() {
  const navigate = useNavigate();
  const [experiences, setExperiences] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/experiences?include_hidden=true").then((r) => setExperiences(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const save = async () => {
    if (!form.title || !form.date) { toast.error("Title and date are required."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        capacity: Number(form.capacity), age_min: Number(form.age_min), age_max: Number(form.age_max),
        member_price: Number(form.member_price), non_member_price: Number(form.non_member_price),
        estimated_cost: Number(form.estimated_cost), transport_price: Number(form.transport_price),
        chaperones_needed: Number(form.chaperones_needed),
        images: form.images ? form.images.split(",").map((s) => s.trim()).filter(Boolean) : [],
        date: new Date(form.date).toISOString(),
      };
      await api.post("/admin/experiences", payload);
      toast.success("Experience created.");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <AppShell admin>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy">Experiences</h1>
          <p className="text-slateBrand">Create and manage all H.E.A. experiences.</p>
        </div>
        <Button data-testid="admin-new-experience-btn" onClick={() => setOpen(true)} className="bg-brandblue font-display font-semibold hover:bg-blue-700"><Plus className="mr-2 h-4 w-4" />New Experience</Button>
      </div>

      <div className="mt-8 space-y-3">
        {experiences.map((e) => (
          <div key={e.id} data-testid={`admin-experience-row-${e.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-4">
              <img src={e.images?.[0]} alt="" className="h-14 w-20 rounded-lg object-cover" />
              <div>
                <div className="font-display font-semibold text-navy">{e.title}</div>
                <div className="text-xs text-slateBrand">{new Date(e.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })} • {e.registered_count}/{e.capacity} registered</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1 text-xs text-slateBrand">{e.visibility === "public" ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}{e.visibility}</span>
              <Button size="sm" variant="outline" data-testid={`admin-manage-btn-${e.id}`} onClick={() => navigate(`/admin/experiences/${e.id}`)}><Settings className="mr-1 h-4 w-4" />Manage</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-navy">New Experience</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Title</Label><Input data-testid="exp-form-title" className="mt-1.5 h-11" value={form.title} onChange={set("title")} placeholder="Marine Discovery Day" /></div>
            <div className="col-span-2"><Label>Short description</Label><Input className="mt-1.5 h-11" value={form.short_description} onChange={set("short_description")} /></div>
            <div className="col-span-2"><Label>Full description</Label><Textarea className="mt-1.5" value={form.description} onChange={set("description")} /></div>
            <div><Label>Category</Label><Input className="mt-1.5 h-11" value={form.category} onChange={set("category")} /></div>
            <div><Label>Subject</Label><Input className="mt-1.5 h-11" value={form.subject} onChange={set("subject")} /></div>
            <div className="col-span-2">
              <Label>Experience type</Label>
              <Select value={form.experience_type} onValueChange={(v) => setForm({ ...form, experience_type: v })}>
                <SelectTrigger data-testid="exp-form-type" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{EXP_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Image URLs (comma-separated)</Label><Input data-testid="exp-form-images" className="mt-1.5 h-11" value={form.images} onChange={set("images")} placeholder="https://…" /></div>
            <div><Label>Date</Label><Input data-testid="exp-form-date" type="datetime-local" className="mt-1.5 h-11" value={form.date} onChange={set("date")} /></div>
            <div><Label>Time (display)</Label><Input className="mt-1.5 h-11" value={form.time} onChange={set("time")} placeholder="9:00 AM - 2:00 PM" /></div>
            <div><Label>Location</Label><Input className="mt-1.5 h-11" value={form.location} onChange={set("location")} /></div>
            <div><Label>Address</Label><Input className="mt-1.5 h-11" value={form.address} onChange={set("address")} /></div>
            <div><Label>Capacity</Label><Input data-testid="exp-form-capacity" type="number" className="mt-1.5 h-11" value={form.capacity} onChange={set("capacity")} /></div>
            <div><Label>Grade range</Label><Input className="mt-1.5 h-11" value={form.grade_range} onChange={set("grade_range")} placeholder="3rd - 8th" /></div>
            <div><Label>Age min</Label><Input type="number" className="mt-1.5 h-11" value={form.age_min} onChange={set("age_min")} /></div>
            <div><Label>Age max</Label><Input type="number" className="mt-1.5 h-11" value={form.age_max} onChange={set("age_max")} /></div>
            <div><Label>Member price ($)</Label><Input data-testid="exp-form-member-price" type="number" className="mt-1.5 h-11" value={form.member_price} onChange={set("member_price")} /></div>
            <div><Label>Non-member price ($)</Label><Input data-testid="exp-form-nonmember-price" type="number" className="mt-1.5 h-11" value={form.non_member_price} onChange={set("non_member_price")} /></div>
            <div><Label>Estimated cost ($)</Label><Input type="number" className="mt-1.5 h-11" value={form.estimated_cost} onChange={set("estimated_cost")} /></div>
            <div><Label>Chaperones needed</Label><Input type="number" className="mt-1.5 h-11" value={form.chaperones_needed} onChange={set("chaperones_needed")} /></div>
            <label className="col-span-2 flex items-center justify-between rounded-lg border border-slate-200 p-3"><span className="text-sm text-navy">Transportation enabled</span><Switch data-testid="exp-form-transport-switch" checked={form.transport_enabled} onCheckedChange={(v) => setForm({ ...form, transport_enabled: v })} /></label>
            {form.transport_enabled && <div className="col-span-2"><Label>Transport price ($ per student)</Label><Input type="number" className="mt-1.5 h-11" value={form.transport_price} onChange={set("transport_price")} /></div>}
            <label className="flex items-center justify-between rounded-lg border border-slate-200 p-3"><span className="text-sm text-navy">Credit eligible</span><Switch checked={form.credit_eligible} onCheckedChange={(v) => setForm({ ...form, credit_eligible: v })} /></label>
            <label className="flex items-center justify-between rounded-lg border border-slate-200 p-3"><span className="text-sm text-navy">May be scholarship eligible</span><Switch data-testid="exp-form-scholarship-switch" checked={form.scholarship_eligible} onCheckedChange={(v) => setForm({ ...form, scholarship_eligible: v })} /></label>
          </div>
          <Button data-testid="exp-form-save" disabled={saving} onClick={save} className="mt-2 h-12 w-full bg-brandblue font-display font-semibold hover:bg-blue-700">{saving ? "Creating…" : "Create Experience"}</Button>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
